import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falComment } from 'shared/@generated/icons/falComment';
import { useRoute } from 'shared/library/routes/route';
import { View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { Button } from 'shared/components/button';

import { useTranslation } from 'react-i18next';
import { brevo } from 'shared/library/brevo';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { ReactNode } from 'react';
import { PurchaseAgreement } from 'shared/components/illustrations/purchase-agreement';
import { Inbox } from 'shared/components/illustrations/inbox';
import { BreederProfile } from 'shared/components/illustrations/breeder-profile';
import { ManagePets } from 'shared/components/illustrations/manage-pets';
import { BreederToolsFeaturesList } from 'shared/components/breeder-tools-features-list';
import { BreederSocialProof } from 'shared/components/breeder-social-proof';
import { PageContent } from 'shared/components/page-content';

interface BreederToolsScreenProps extends Props {}

type FeatureKey =
  | 'breeder_profile'
  | 'pet_management'
  | 'documents_certificates'
  | 'buyer_communication';

const BreederToolsScreen = ({}: BreederToolsScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();

  const features: { key: FeatureKey; image: ReactNode }[] = [
    { key: 'breeder_profile', image: <BreederProfile /> },
    { key: 'pet_management', image: <ManagePets /> },
    { key: 'documents_certificates', image: <PurchaseAgreement /> },
    { key: 'buyer_communication', image: <Inbox /> },
  ];

  return (
    <PageContent nativeSafeTop={true}>
      <View className={'items-center gap-6'} testID={'breeder-tools-intro'}>
        <View className={'w-56 md:w-64'}>
          <BreederProfile />
        </View>

        <View className={'w-full max-w-3xl'}>
          <H1
            title={t('breeder_tools.title')}
            subtitle={t('breeder_tools.description')}
            center={true}
          />

          <View
            className={
              'mx-auto mt-3 w-full max-w-sm gap-1.5 md:mt-6 md:w-auto md:max-w-none md:flex-row md:justify-center md:gap-3'
            }
          >
            <Button
              text={t('breeder_tools.create_breeder_account')}
              icon={falAngleRight}
              color={'brown'}
              href={route('me.breeder.index')}
              center={true}
            />

            <Button
              text={t('common:help')}
              icon={falComment}
              iconPosition={'left'}
              color={'white'}
              onPress={() => brevo.openChat()}
              center={true}
            />
          </View>

          <BreederSocialProof
            prefix={t('breeder_tools.social_proof.prefix')}
            count={t('breeder_tools.social_proof.count')}
            suffix={t('breeder_tools.social_proof.suffix')}
            className={'mx-auto mt-4 w-full max-w-sm flex-row items-center justify-center gap-3'}
          />
        </View>
      </View>

      <View className={'mt-6 md:my-12'} />

      <H2
        title={t('breeder_tools.features')}
        subtitle={t('breeder_tools.features_description')}
        center={true}
      />

      <BreederToolsFeaturesList
        items={features.map((feature) => ({
          title: t(`breeder_tools.tools.${feature.key}`),
          text: t(`breeder_tools.tools.${feature.key}_description`),
          image: feature.image,
        }))}
      />

      <View className={'mt-6 md:my-12'} />

      <ReviewsSegment role={'seller'} />
    </PageContent>
  );
};

export { BreederToolsScreen };
export type { BreederToolsScreenProps };
